import {FC, ReactNode} from 'react'
import {useIsMobile} from '../hooks/useIsMobile'

const INK = '#1b1b29'
const MUTED = '#7e8299'

/**
 * Branded page header — a rounded gradient banner mirroring the public site's
 * PageHero, with an optional icon and a right-aligned action slot. Shared by
 * renter dashboard and admin panel pages.
 */
export const PageHead: FC<{
  title: string
  sub?: string
  eyebrow?: string
  icon?: string
  action?: ReactNode
}> = ({title, sub, eyebrow, icon, action}) => {
  const isMobile = useIsMobile()
  return (
  <div
    className='ccp-fade-up'
    style={{
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #013d1f 0%, #039540 58%, #05b34d 100%)',
      color: '#fff',
      borderRadius: 18,
      padding: isMobile ? '20px 18px' : '26px 30px',
      marginBottom: 24,
      boxShadow: '0 10px 26px rgba(27,27,41,0.10)',
    }}
  >
    <div className='ccp-blob' style={{background: '#0ad66a', width: 230, height: 230, top: -130, right: '6%'}} />
    <div className='ccp-blob' style={{background: '#016b2f', width: 200, height: 200, bottom: -150, left: '20%'}} />
    <div style={{position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'}}>
      <div style={{display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, minWidth: 0}}>
        {icon && (
          <div style={{width: isMobile ? 44 : 56, height: isMobile ? 44 : 56, borderRadius: 14, background: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? 22 : 28, flexShrink: 0}}>
            {icon}
          </div>
        )}
        <div style={{minWidth: 0}}>
          {eyebrow && <div style={{fontSize: isMobile ? 11 : 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.85, marginBottom: isMobile ? 4 : 6}}>{eyebrow}</div>}
          <h1 style={{color: '#fff', fontSize: isMobile ? 21 : 27, fontWeight: 800, margin: 0, lineHeight: 1.15, overflowWrap: 'anywhere'}}>{title}</h1>
          {sub && <p style={{fontSize: isMobile ? 13.5 : 15, opacity: 0.95, margin: isMobile ? '6px 0 0' : '8px 0 0', maxWidth: 580}}>{sub}</p>}
        </div>
      </div>
      {action && <div style={{position: 'relative', flexShrink: 0}}>{action}</div>}
    </div>
  </div>
  )
}

/** White rounded surface card with the public-site lift-on-hover. */
export const Surface: FC<{children: ReactNode; style?: React.CSSProperties; lift?: boolean}> = ({children, style, lift}) => (
  <div className={lift ? 'ccp-lift' : undefined} style={{background: '#fff', borderRadius: 16, boxShadow: '0 8px 24px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', ...style}}>
    {children}
  </div>
)

export const SectionTitle: FC<{children: ReactNode}> = ({children}) => (
  <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: '0 0 14px'}}>{children}</h2>
)

export const Muted: FC<{children: ReactNode}> = ({children}) => (
  <span style={{color: MUTED}}>{children}</span>
)
